import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import {
  Avatar,
  Button,
  Checkbox,
  CircularProgress,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useTheme
} from '@mui/material';
import { Box } from '@mui/system';
import { useFormik } from 'formik';
import validationSchema from './validationSchema';
import SearchIcon from '@mui/icons-material/Search';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import { IClipping } from 'src/models/onlineMedia';
import CloseIcon from '@mui/icons-material/Close';
import ModalAddAutoClipping from 'src/components/ModalAddAutoClipping';
import useOnlineMedia from 'src/services/useOnlineMedia';
import { useAppSelector } from 'src/app/hooks';
import { useEffect, useState } from 'react';
import { useDebounce } from 'src/hooks/useDebounce';
import ModalNewLabel from 'src/components/ModalNewLabel';

export interface IClippingAs {
  attachClippings: [];
  removeClippings: [];
}

interface Props {
  onClose: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;
  open: boolean;
  clippingSelectedList?: Array<Array<IClipping>>;
  onSubmit: (payload: IClippingAs) => void;
}

const TableCellHeader = styled(TableCell)(
  ({ theme }) => `
      z-index: 3;
      background: ${theme.palette.primary.main};
      color: ${theme.palette.common.white};
  `
);

const ModalClippingAsRetail: React.FC<Props> = ({
  onClose,
  open,
  clippingSelectedList,
  onSubmit
}) => {
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const projectId = useAppSelector((state) => state.users.project.id);
  const {
    result: clippingList,
    limit,
    totalPages
  } = useAppSelector((state) => state.storeOnlineMediaStream.clippingList.data);

  const loading = useAppSelector(
    (state) => state.storeOnlineMediaStream.clippingList.loading
  );

  const [debouncedState, setDebouncedState] = useDebounce();
  const { getClippingsV5 } = useOnlineMedia();
  const { handleSubmit, setFieldValue, values, resetForm } =
    useFormik<IClippingAs>({
      initialValues: {
        attachClippings: [],
        removeClippings: []
      },
      validationSchema: validationSchema(),
      onSubmit: (value) => {
        onSubmit(value);
        resetForm({
          values: {
            attachClippings: [],
            removeClippings: []
          }
        });
        setDebouncedState('');
      }
    });

  const statusClipping = function (clipping: IClipping) {
    let status = '';
    const available: Array<IClipping> = [];
    clippingSelectedList.forEach((stream) => {
      stream.forEach(
        (clippingSelected: IClipping) =>
          clippingSelected.id === clipping.id &&
          available.push(clippingSelected)
      );
    });

    if (available.length < 1) {
      status = 'unChecked';
    }
    if (
      available.length > 0 &&
      available.length < clippingSelectedList.length
    ) {
      status = 'indeterminate';
    }
    if (available.length === clippingSelectedList.length) {
      status = 'checked';
    }
    return status;
  };

  const handleClose = (
    event: {},
    reason?: 'backdropClick' | 'escapeKeyDown'
  ) => {
    setDebouncedState('');
    onClose(event, reason);
    resetForm({
      values: {
        attachClippings: [],
        removeClippings: []
      }
    });
  };

  const handleSearch = (value: string) => {
    setDebouncedState(value);
  };

  const handleClickPage = (page: number) => {
    getClippingsV5({
      projectId,
      page,
      limit,
      name: debouncedState ?? ''
    });
    setPage(page);
  };

  // reset page to 1 when debounced state changed
  useEffect(() => {
    getClippingsV5({
      projectId,
      page: 1,
      limit: limit,
      name: debouncedState ?? ''
    });
    setPage(1);
  }, [debouncedState]);

  return (
    <Dialog onClose={handleClose} open={open} fullWidth maxWidth={'sm'}>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle
        sx={{
          m: theme.spacing(3, 3, 1)
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h3">Clip as</Typography>
          <ModalNewLabel variantButton="outlined" />
          {/* <Button variant="outlined" size="small" endIcon={<AddIcon />}>
            New Clipping
          </Button> */}
          <ModalAddAutoClipping />
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          m: theme.spacing(1, 3)
        }}
      >
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            id="standard-basic"
            placeholder="Search Profile"
            variant="standard"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{
              mb: theme.spacing(3)
            }}
            onChange={(e) => handleSearch(e.target.value)}
          />

          <Stack direction="column" gap={2}>
            <TableContainer
              component={Paper}
              sx={{ maxHeight: theme.spacing(30) }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCellHeader
                      sx={{ width: '5%', padding: 0 }}
                      padding="none"
                    >
                      <Checkbox
                        // indeterminate={
                        //   values.attachClippings.length > 0 &&
                        //   values.attachClippings.length < labels.length
                        // }
                        size="small"
                        sx={{ ml: theme.spacing(0.5) }}
                        icon={
                          <FilterNoneIcon
                            sx={{
                              color: theme.palette.common.white,
                              width: theme.spacing(2.2),
                              height: theme.spacing(2.2)
                            }}
                          />
                        }
                        checkedIcon={
                          <LibraryAddCheckIcon
                            sx={{
                              color: theme.palette.common.white,
                              width: theme.spacing(2.2),
                              height: theme.spacing(2.2)
                            }}
                          />
                        }
                        onChange={(e) => {
                          if (!e.target.checked) {
                            setFieldValue('attachClippings', []);
                          } else {
                            setFieldValue(
                              'attachClippings',
                              clippingList.map((clipping) => clipping.id)
                            );
                          }
                        }}
                      />
                    </TableCellHeader>
                    <TableCellHeader align="left" size="small">
                      Clip
                    </TableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow hover={false}>
                      <TableCell align="center" colSpan={2}>
                        <Box
                          width="100%"
                          height="100%"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <CircularProgress disableShrink size={32} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : clippingList.length <= 0 ? (
                    <TableRow hover={false}>
                      <TableCell align="center" colSpan={2}>
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          <Avatar>N/A</Avatar>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    clippingList.map((clipping) => {
                      const status = statusClipping(clipping);

                      const attach = values.attachClippings.find(
                        (attach) => attach === clipping.id
                      );
                      const remove = values.removeClippings.find(
                        (remove) => remove === clipping.id
                      );
                      let indeterminate = false;
                      if (status === 'indeterminate') {
                        if (!attach && !remove) {
                          indeterminate = true;
                        }
                      }
                      let isChecked = false;
                      if (!indeterminate && status === 'checked') {
                        isChecked = true;
                      }
                      if (indeterminate) {
                        isChecked = true;
                      } else {
                        if (attach) {
                          isChecked = true;
                        }
                        if (remove) {
                          isChecked = false;
                        }
                      }
                      return (
                        <TableRow key={clipping.id}>
                          <TableCell
                            align="center"
                            size="small"
                            sx={{ padding: 0 }}
                          >
                            <FormControlLabel
                              sx={{ marginLeft: 0 }}
                              control={
                                <Checkbox
                                  size="small"
                                  indeterminate={indeterminate}
                                  checked={isChecked}
                                  onChange={(e) => {
                                    if (status === 'checked') {
                                      if (!e.target.checked) {
                                        setFieldValue('removeClippings', [
                                          ...values.removeClippings,
                                          clipping.id
                                        ]);
                                      } else {
                                        const filter =
                                          values.removeClippings.filter(
                                            (remove) => remove !== clipping.id
                                          );
                                        setFieldValue(
                                          'removeClippings',
                                          filter
                                        );
                                      }
                                    }
                                    if (status === 'unChecked') {
                                      if (e.target.checked) {
                                        setFieldValue('attachClippings', [
                                          ...values.attachClippings,
                                          clipping.id
                                        ]);
                                      } else {
                                        const filter =
                                          values.attachClippings.filter(
                                            (remove) => remove !== clipping.id
                                          );
                                        setFieldValue(
                                          'attachClippings',
                                          filter
                                        );
                                      }
                                    }
                                    if (status === 'indeterminate') {
                                      if (!e.target.checked && indeterminate) {
                                        setFieldValue('attachClippings', [
                                          ...values.attachClippings,
                                          clipping.id
                                        ]);
                                        const filter =
                                          values.removeClippings.filter(
                                            (remove) => remove !== clipping.id
                                          );
                                        setFieldValue(
                                          'removeClippings',
                                          filter
                                        );
                                      }
                                      if (!e.target.checked && !indeterminate) {
                                        const filter =
                                          values.attachClippings.filter(
                                            (remove) => remove !== clipping.id
                                          );
                                        setFieldValue(
                                          'attachClippings',
                                          filter
                                        );
                                        setFieldValue('removeClippings', [
                                          ...values.removeClippings,
                                          clipping.id
                                        ]);
                                      }
                                      if (e.target.checked && !indeterminate) {
                                        const filter =
                                          values.removeClippings.filter(
                                            (remove) => remove !== clipping.id
                                          );
                                        setFieldValue(
                                          'removeClippings',
                                          filter
                                        );
                                      }
                                    }
                                  }}
                                  name="labels"
                                  color="primary"
                                  value={clipping.id}
                                />
                              }
                              label=""
                            />
                          </TableCell>
                          <TableCell align="left" size="small">
                            {clipping.name}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box>
              <Pagination
                count={totalPages}
                page={page}
                shape="rounded"
                color="primary"
                onChange={(_, page) => handleClickPage(page)}
                sx={{ '& .MuiPagination-ul': { justifyContent: 'right' } }}
              />
            </Box>
          </Stack>
          <Stack
            direction="row"
            justifyContent="flex-end"
            sx={{ marginTop: theme.spacing(3) }}
          >
            <Button
              variant="contained"
              type="submit"
              size="large"
              sx={{
                background: theme.palette.primary.dark,
                width: theme.spacing(20)
              }}
            >
              Done
            </Button>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ModalClippingAsRetail;
